import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Select from 'react-select';
import {connect} from 'react-redux';
import {toast} from 'react-toastify';
import {X} from 'lucide-react';

import * as Actions from '../../../store/actions';
import {
  Constants,
  handleFileInput,
  capitalizeEveryWord,
} from '../../../helpers';
import {useAllApps} from '../../../hooks/useApps';
import useDragAndDrop from '../../../hooks/useDragAndDrop';

import {
  customStyles,
  DropdownIndicator,
  PrimaryButton,
  Modal,
  CreateAppForm,
  TableLayout,
  Loader,
} from '../../../components';
import {
  plus,
  times,
  uploadCsv,
  closeModal,
} from '../../../assets/images/images';
import {getDownloadUrl} from '../../../helpers/batchTemplateDownloadUrl';

function BatchBusinessLookupModal({
  performBatchLookup,
  changeLookupPage,
  downloadBatchLookup,
  auth: {userDetails},
  lookup: {lookupPage, loading, l_loading, singleBatch},
  open,
  setOpen,
}) {
  const {userApps, isLoading} = useAllApps();
  const {dragOver, setDragOver, onDragOver, onDragLeave} = useDragAndDrop();

  const nigeriaDefault = Constants.lookups.countries.find(
    c => c.value === 'nigeria',
  );

  const [lookupInputs, setLookupInputs] = useState({
    ...Constants?.lookups?.initialLookUpState,
    app: (!isLoading && userApps && userApps[0]) || {},
    country: nigeriaDefault,
  });
  const [step, setStep] = useState(lookupPage !== undefined ? lookupPage : 1);
  const [file, setFileData] = useState(null);
  const [enable, setEnable] = useState(true);
  const [newAppForm, setNewAppForm] = useState(false);

  useEffect(() => {
    setLookupInputs(prev => ({
      ...prev,
      app: (!isLoading && userApps && userApps[0]) || {},
    }));
  }, [userApps, isLoading]);

  useEffect(() => {
    setStep(lookupPage !== undefined ? lookupPage : 1);
  }, [lookupPage]);

  const {country, type, app} = useMemo(() => lookupInputs, [lookupInputs]);

  const {downloadIndividualLookup: canDownloadHistory} =
    useMemo(() => userDetails?.permissions?.lookupPermissions, [userDetails]) ||
    {};

  const handleLookup = useCallback(() => {
    if (!app.value) return toast.error('Please select an app');
    if (file) {
      const path = Constants.lookups.pathUrlTypeCheck(type.value);
      delete file.type;
      performBatchLookup(
        path,
        {...file, app_id: app.value},
        app.value,
        setOpen,
      );
    }
  }, [app.value, file, type, performBatchLookup, setOpen]);

  const onDrop = e => {
    e.preventDefault();
    setDragOver(false);
    const selectedFile = e?.dataTransfer?.files[0];
    if (
      !selectedFile.type.includes('sheet') &&
      !selectedFile.type.includes('csv')
    ) {
      return toast.error('Please provide a valid file type (.xlsx, .csv)');
    }
    handleFileInput(selectedFile, setFileData);
  };

  useEffect(() => {
    setEnable(!file);
  }, [file]);

  const businessIds = useMemo(() => ['cac', 'cac/basic'], []);

  const getFilteredOptions = useMemo(() => {
    const allOptions = Constants.lookups.getIDType(country?.value || 'nigeria');
    let filtered = allOptions?.filter(option =>
      businessIds.includes(option?.value),
    );

    if (country?.value === 'nigeria') {
      filtered = filtered.map(option => {
        if (option?.value === 'cac') {
          return {...option, label: 'CAC Advance'};
        }
        return option;
      });
    }
    return filtered;
  }, [country?.value, businessIds]);

  const batchUploadProgressPercentage = Math.round(
    (singleBatch?.processed_records / singleBatch?.total_records || 0) * 100,
  );

  const getModalClasses = () => {
    const baseClasses = 'overflow-y-auto absolute bottom-0 sm:relative';
    if (step === 1)
      return `max-w-[528px] h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg ${baseClasses} lookup-modal`;
    return `max-w-[516px] rounded-b-none sm:rounded-b-lg h-fit max-h-full ${baseClasses}`;
  };

  const renderFormStep = () => (
    <div>
      <header className="flex items-center bg-[#F9F9F9] justify-between py-3 px-6 rounded-t-xl border-[0.5px] border-transparent border-b-[#EFEFEF]">
        <h3 className="font-semibold text-deepBlue text-[18px]">
          Batch Business Lookup
        </h3>
        <button
          onClick={() => setOpen(false)}
          className="bg-[#EDEDED] rounded-md p-1.5"
        >
          <X size={16} color="#242424" />
        </button>
      </header>
      <form className="text-left" style={{opacity: l_loading && 0.4}}>
        <div className="py-4 px-6">
          <label htmlFor="app" className="relative mb-1 mt-6">
            Select app
          </label>
          <Select
            value={lookupInputs?.app || (!isLoading && userApps[0])}
            onChange={app => setLookupInputs({...lookupInputs, app})}
            options={userApps}
            placeholder="Select app"
            components={{DropdownIndicator}}
            classNamePrefix="react-select"
            styles={customStyles}
            isDisabled={isLoading}
          />
          <button
            className="flex items-center gap-[2px] ml-auto mt-2"
            onClick={() => setNewAppForm(p => !p)}
            type="button"
          >
            <img
              src={newAppForm ? times : plus}
              alt=""
              width={12}
              height={12}
            />
            <span className="text-xs font-medium text-body">
              {newAppForm ? 'Close' : 'Create a new app'}
            </span>
          </button>
          {newAppForm && (
            <div className="p-6 mt-2 rounded bg-white80 animate-dropdown">
              <h3 className="text-base font-medium text-grey">Create App</h3>
              <CreateAppForm setOpen={() => setNewAppForm(p => !p)} />
            </div>
          )}
          <label htmlFor="id_type" className="relative mt-6 mb-1">
            Select ID Type
          </label>
          <Select
            value={lookupInputs?.type}
            onChange={type => setLookupInputs({...lookupInputs, type})}
            options={getFilteredOptions}
            placeholder="Select an ID type"
            components={{DropdownIndicator}}
            classNamePrefix="react-select"
            styles={customStyles}
            menuPlacement="auto"
            isSearchable={false}
            key={`business-batch-${country?.value}`}
          />

          <div
            className={`${
              lookupInputs.type === null ? 'hidden' : 'block'
            } mt-8 sm:bg-white80 sm:p-6 rounded`}
          >
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              style={{border: `${dragOver ? '3px dashed #33333322' : ''}`}}
              className="mt-6 py-[22px] flex flex-col items-center gap-[2px] text-center border border-brandBlue border-dashed rounded"
            >
              <div className="flex-col items-center hidden sm:flex">
                {!file && <img src={uploadCsv} alt="" width={48} height={48} />}
                <p className="text-body">
                  {dragOver ? (
                    'Please drop file here'
                  ) : (
                    <span style={{color: file ? '#00000032' : ''}}>
                      Drag and drop Excel file here
                    </span>
                  )}
                </p>
              </div>
              <div className="text-body text-sm flex gap-[3px] items-baseline">
                {file ? (
                  <label htmlFor="file">Change file</label>
                ) : (
                  <>
                    <span className="hidden sm:block">or</span>
                    <label htmlFor="file">click here to select</label> from your
                    device
                  </>
                )}
                <input
                  type="file"
                  id="file"
                  onChange={e =>
                    handleFileInput(e.target.files[0], setFileData)
                  }
                  accept=".xlsx, .csv"
                />
              </div>
              {file && (
                <p className="text-body text-sm">
                  File:{' '}
                  <span className="font-bold text-black">{file.filename}</span>
                </p>
              )}
            </div>
            {['cac', 'basic'].includes(type?.value) && (
              <div className="mt-2 text-xs text-right">
                <a
                  href={getDownloadUrl(type?.value)}
                  target="_blank"
                  rel="noreferrer"
                  download
                  className="font-medium text-body sm:text-brandBlue"
                >
                  Click here{' '}
                  <span className="font-medium text-body">
                    to download template for batch upload
                  </span>
                </a>
              </div>
            )}
          </div>
        </div>
        <div className="w-full px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
          <PrimaryButton
            disabled={enable}
            buttonText="Lookup"
            onClick={handleLookup}
            className="w-full !h-10"
            loading={loading}
          />
        </div>
      </form>
    </div>
  );

  const renderProgressStep = () => (
    <div className="px-4 pt-10 pb-4 sm:p-10">
      <header className="flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-medium text-grey">
            {capitalizeEveryWord(singleBatch?.lookup_type.replace('_', ' '))}{' '}
            Verification
          </h3>
          <div className="px-2 py-1 text-xs font-medium uppercase rounded-full text-brandBlue50 bg-brandBlue40 w-fit">
            Batch upload
          </div>
          <div
            className={`px-2 py-1 text-xs font-medium uppercase rounded-full w-fit ${
              batchUploadProgressPercentage === 100
                ? 'text-success20 bg-success'
                : 'text-white bg-status-404'
            }`}
          >
            {batchUploadProgressPercentage === 100
              ? 'Successful'
              : 'Processing'}
          </div>
        </div>
        <button
          onClick={() => {
            changeLookupPage(1);
            setOpen(false);
          }}
          className="hidden sm:block"
        >
          <img src={closeModal} alt="" />
        </button>
      </header>
      <div
        className={`relative h-4 mt-4 mb-8 text-xs font-medium ${
          batchUploadProgressPercentage > 50 && 'text-white'
        } rounded-full bg-brandBlue40`}
      >
        <span className="absolute left-0 right-0 top-1">
          {(!l_loading && batchUploadProgressPercentage) || 0}%
        </span>
        <progress
          id="file"
          value={(!l_loading && batchUploadProgressPercentage) || 0}
          max="100"
        ></progress>
      </div>
      {l_loading ? (
        <Loader height={30} />
      ) : (
        <TableLayout>{/* ... Table ... */}</TableLayout>
      )}
      <div className="flex flex-col items-center gap-4 mt-6 sm:flex-row">
        <button className="flex items-center justify-between sm:justify-start gap-4 py-[14px] px-4 text-tiny text-grey80 bg-white80 outline outline-1 outline-grey60 rounded w-full sm:w-fit focus-visible:outline-brandBlue">
          XLSX
        </button>
        <PrimaryButton
          buttonText="Download"
          className="flex-1 w-full py-4 rounded sm:w-fit"
          onClick={() =>
            downloadBatchLookup(singleBatch?.id, () => {
              setOpen(false);
              changeLookupPage(1);
            })
          }
          loading={loading}
          disabled={!canDownloadHistory}
          title={
            canDownloadHistory
              ? 'Download history'
              : 'You do not have permission to download lookup history'
          }
        />
      </div>
    </div>
  );

  const renderModalContent = () => {
    switch (step) {
      case 1:
        return renderFormStep();
      case 3:
        return renderProgressStep();
      default:
        return null;
    }
  };

  return (
    <Modal
      show={open}
      modalPosition="justify-center sm:justify-end"
      onClose={setOpen}
      extraModalClasses={getModalClasses()}
    >
      {renderModalContent()}
    </Modal>
  );
}

export default connect(state => state, Actions)(BatchBusinessLookupModal);
